import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { createTranslator, useLanguage } from '../i18n/LanguageContext';
import { publicViewsTranslations } from '../i18n/translations';

const FUNCTIONS_BASE_URL = 'https://us-central1-mendyx-adafe.cloudfunctions.net';

const functionUrl = (name, params = {}) => {
  const url = new URL(`${FUNCTIONS_BASE_URL}/${name}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, value);
    }
  });
  return url.toString();
};

const postFunctionJson = async (name, body) => {
  const response = await fetch(`${FUNCTIONS_BASE_URL}/${name}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || 'Erreur serveur');
    error.data = data;
    error.status = response.status;
    throw error;
  }
  return data;
};

const getTeamName = (team) => team?.name || team || '';
const getTeamScore = (team) => Number(team?.score ?? 0);

const TournamentViewUsers = () => {
  const { language } = useLanguage();
  const t = createTranslator(language, publicViewsTranslations);
  const { tournamentId } = useParams();
  const [pools, setPools] = useState([]);
  const [matches, setMatches] = useState([]);
  const [showPools, setShowPools] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const applyTournamentData = useCallback((data) => {
    const publicPools = data.pools || [];
    const publicMatches = data.matches || [];

    setPools(publicPools);
    setMatches(publicMatches);
    setShowPools(
      publicPools.reduce((acc, _, index) => {
        acc[index] = true;
        return acc;
      }, {})
    );
  }, []);

  const fetchTournamentData = useCallback(async () => {
    const response = await fetch(
      functionUrl('protectedGetTournamentPublicView', { tournamentId })
    );
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Erreur serveur');
    }

    applyTournamentData(data);
    return data;
  }, [applyTournamentData, tournamentId]);

  useEffect(() => {
    const loadTournament = async () => {
      try {
        setLoading(true);
        const data = await fetchTournamentData();

        if ((data.players || []).length > 0 && (data.pools || []).length === 0) {
          setGenerating(true);
          const result = await postFunctionJson('protectedGenerateTournamentPublicView', {
            tournamentId,
          });

          if (result.success) {
            await fetchTournamentData();
          }
        }
      } catch (error) {
        if (error.data?.reason === 'notFull') {
          console.log(
            `Tournoi non complet (${error.data.participantsCount}/${error.data.participantsLimit})`
          );
        } else {
          console.error('Erreur de chargement des données du tournoi:', error);
        }
      } finally {
        setGenerating(false);
        setLoading(false);
      }
    };

    loadTournament();
  }, [fetchTournamentData, tournamentId]);

  const togglePoolVisibility = (poolIndex) => {
    setShowPools((prev) => ({ ...prev, [poolIndex]: !prev[poolIndex] }));
  };

  const getPoolRanking = (poolIndex) => {
    const stats = {};

    matches
      .filter((match) => Number(match.poolIndex ?? 0) === poolIndex)
      .forEach((match) => {
        const teamAName = getTeamName(match.teamA);
        const teamBName = getTeamName(match.teamB);
        const teamAScore = getTeamScore(match.teamA);
        const teamBScore = getTeamScore(match.teamB);

        if (!teamAName || !teamBName) return;

        if (!stats[teamAName]) {
          stats[teamAName] = { points: 0, wins: 0, gamesWon: 0, gamesLost: 0 };
        }
        if (!stats[teamBName]) {
          stats[teamBName] = { points: 0, wins: 0, gamesWon: 0, gamesLost: 0 };
        }

        stats[teamAName].gamesWon += teamAScore;
        stats[teamAName].gamesLost += teamBScore;
        stats[teamBName].gamesWon += teamBScore;
        stats[teamBName].gamesLost += teamAScore;

        if (teamAScore > teamBScore) {
          stats[teamAName].points += 3;
          stats[teamAName].wins += 1;
        } else if (teamBScore > teamAScore) {
          stats[teamBName].points += 3;
          stats[teamBName].wins += 1;
        }
      });

    return Object.entries(stats)
      .map(([teamName, data]) => ({
        teamName,
        ...data,
        diff: data.gamesWon - data.gamesLost,
      }))
      .sort((a, b) => b.points - a.points || b.diff - a.diff);
  };

  if (loading) {
    return (
      <div className="container mx-auto my-16 px-4 text-center text-gray-600">
        {generating ? t('generatingPools') : t('loadingData')}
      </div>
    );
  }

  return (
    <section className="container mx-auto my-16 px-4">
      <h2 className="text-3xl font-semibold text-[#4c2f8e] mb-8 text-center">
        {t('poolsView')}
      </h2>

      {pools.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          {pools.map((pool, poolIndex) => {
            const poolMatches = matches
              .filter((match) => Number(match.poolIndex ?? 0) === poolIndex)
              .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0));
            const ranking = getPoolRanking(poolIndex);

            return (
              <div
                key={poolIndex}
                className="p-6 bg-white shadow-lg rounded-xl border border-gray-200"
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-2xl font-semibold text-[#4c2f8e] mb-4">
                    {t('pool', { number: poolIndex + 1 })}
                  </h3>
                  <button onClick={() => togglePoolVisibility(poolIndex)}>
                    {showPools[poolIndex] ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </div>

                {showPools[poolIndex] && (
                  <>
                    <div className="border-b border-gray-200 pb-4 mb-4">
                      <ul className="space-y-2">
                        {pool.map((player, index) => (
                          <li
                            key={`${player.teamName || index}-${index}`}
                            className="text-black text-lg bg-gray-50 px-4 py-2 rounded-lg shadow-md"
                          >
                            {player.teamName}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <h4 className="text-xl font-semibold text-[#4c2f8e] mb-4">
                      {t('poolRanking')}
                    </h4>
                    <ul className="space-y-4 mb-6">
                      {ranking.map((team, index) => (
                        <li key={team.teamName} className="text-lg bg-gray-100 p-4 rounded-lg shadow">
                          {team.teamName} - {team.points} P - {team.wins} V - {team.gamesWon} JG -{' '}
                          {team.gamesLost} JP (Diff: {team.diff})
                        </li>
                      ))}
                      {ranking.length === 0 && (
                        <li className="text-gray-500">{t('noScore')}</li>
                      )}
                    </ul>

                    <h4 className="text-xl font-semibold text-[#4c2f8e] mb-4">{t('matches')}</h4>
                    <ul className="space-y-4">
                      {poolMatches.map((match, matchIndex) => (
                        <li
                          key={match.id || matchIndex}
                          className="text-black bg-white px-4 py-3 rounded-lg shadow-md border"
                        >
                          <div className="flex justify-between">
                            <div>
                              <span className="font-semibold text-black">
                                {getTeamName(match.teamA)}
                              </span>{' '}
                              vs{' '}
                              <span className="font-semibold text-black">
                                {getTeamName(match.teamB)}
                              </span>
                              {(match.teamA?.score !== undefined ||
                                match.teamB?.score !== undefined) && (
                                <span className="text-sm text-gray-500 ml-2">
                                  ({getTeamScore(match.teamA)} - {getTeamScore(match.teamB)})
                                </span>
                              )}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-gray-600 text-center">
          {t('poolsPending')}
        </p>
      )}
    </section>
  );
};

export default TournamentViewUsers;
